import { FC } from 'react';
import dayjs from 'dayjs';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Fragment } from 'react/jsx-runtime';

// MUI
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  dishNoteSuffix,
  NutritionMenuFilterModel,
  NutritionMenuModel,
} from 'modules/map/components/nutrition-menu/types';

import { formatUTC } from 'modules/map/components/pdf-print-form/utils';
import NotFoundBox from 'common/components/NotFoundBox';

interface Props {
  data: NutritionMenuModel;
  filter: NutritionMenuFilterModel;
}

const MenuTable: FC<Props> = (props) => {
  const { data, filter } = props;
  const intl = useIntl();

  const days = data?.structuredData?.data?.items || [];
  const isAllEmpty = days.every((day) => day.dishes.length === 0);

  const fromDate = formatUTC(days[0]?.datetime, 'L');
  const toDate = formatUTC(days[days.length - 1]?.datetime, 'L');

  const isNutritionMenu = data?.structuredData.metadata.isNutritionMenu;
  const shouldShowAfternoonMeals = data?.structuredData.metadata.hasAfternoon;

  const tableHeader = isNutritionMenu
    ? [
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.day" />,
        },
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.menu" />,
        },
        {
          rowSpan: 1,
          colSpan: 2,
          label: <FormattedMessage id="map.dishQuantity" />,
          width: '20%',
        },
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.energyLunch" />,
          width: '8%',
        },
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.protein" />,
          width: '8%',
        },
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.lipid" />,
          width: '8%',
        },
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.gluxit" />,
          width: '8%',
        },
        shouldShowAfternoonMeals
          ? {
              rowSpan: 2,
              label: <FormattedMessage id="map.afternoon" />,
            }
          : null,
        shouldShowAfternoonMeals
          ? {
              rowSpan: 2,
              label: <FormattedMessage id="map.energyAfternoon" />,
              width: '6%',
            }
          : null,
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.note" />,
          width: '8%',
        },
      ]
    : [
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.day" />,
          width: '10%',
        },
        {
          rowSpan: 2,
          label: <FormattedMessage id="map.menu" />,
          width: '25%',
        },
        {
          rowSpan: 1,
          colSpan: 2,
          label: <FormattedMessage id="map.dishQuantity" />,
          width: '30%',
        },
        shouldShowAfternoonMeals
          ? {
              rowSpan: 2,
              label: <FormattedMessage id={'map.afternoon'} />,
              width: '16%',
            }
          : null,
        {
          rowSpan: 2,
          colSpan: 2,
          label: <FormattedMessage id="map.note" />,
          width: '10%',
        },
      ];

  if (isAllEmpty) return <NotFoundBox />;

  return (
    <TableContainer
      className="px-2 tablet:px-4"
      sx={{
        mt: 2,
        '& td,th': {
          border: '0.5px solid',
          textAlign: 'center',
          padding: 1,
        },
        '@media print': {
          '& table': {
            borderCollapse: 'collapse',
          },
          '& td,th': {
            background: 'none',
            color: 'black',
          },
          '& thead': {
            background: 'none',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'none',
          '@media print': {
            display: 'flex',
            justifyContent: 'space-around',
            color: 'black',
            marginBottom: 2,
          },
        }}
      >
        <Typography variant="body1">{dayjs().format('LL')}</Typography>
        <Typography variant="h6" textAlign={'center'} flex={1}>
          <FormattedMessage id="map.nutritionMenu" /> &nbsp;(
          {[fromDate, toDate].join(' - ')})
        </Typography>
        <Typography variant="h6">
          <FormattedMessage id="map.process" />
          :&nbsp;
          {intl.formatMessage({ id: 'map.' + filter.state })}
        </Typography>
      </Box>
      <Table
        sx={(theme) => ({
          borderCollapse: 'separate',
          '& th, & td': { borderRight: `1px solid ${theme.palette.divider}` },
        })}
      >
        <TableHead
          sx={(theme) => ({
            position: 'sticky',
            top: 0,
            background: theme.palette.background.paper,
          })}
        >
          <TableRow>
            {tableHeader
              .filter((item) => item !== null)
              .map((item, index) => {
                return (
                  <TableCell
                    key={index}
                    rowSpan={item.rowSpan}
                    colSpan={item.colSpan}
                    className="whitespace-pre-wrap"
                    style={{ width: item.width }}
                  >
                    {item.label}
                  </TableCell>
                );
              })}
          </TableRow>
          <TableRow>
            {[
              {
                rowSpan: 1,
                label: <FormattedMessage id="map.rawUnit" />,
                width: isNutritionMenu ? '10%' : '15%',
              },
              {
                rowSpan: 1,
                label: <FormattedMessage id="map.cookedUnit" />,
                width: isNutritionMenu ? '10%' : '15%',
              },
            ].map((item, index) => {
              return (
                <TableCell key={index} rowSpan={item.rowSpan} style={{ width: item.width }}>
                  {item.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((day, idx) => {
            const dishesCount = day.dishes.length;

            const shouldBreakNotes = day.note.includes('\n');
            const noteOfTheDay = shouldBreakNotes
              ? day.note.split('\n').map((note, index) => {
                  return (
                    <Typography key={index} sx={{ my: 1 }}>
                      {note}
                    </Typography>
                  );
                })
              : day.note;

            return (
              <Fragment key={idx}>
                {day.dishes.map((dish, index) => {
                  const isFirstRow = index === 0;

                  return (
                    <TableRow key={index}>
                      {isFirstRow && (
                        <TableCell rowSpan={dishesCount}>
                          <span className="capitalize">{formatUTC(day.datetime, 'dddd')}</span>
                          <br />
                          {day.datetime && formatUTC(day.datetime, 'L')}
                        </TableCell>
                      )}
                      <TableCell>
                        {dish.name}
                        {dishNoteSuffix(dish)}
                      </TableCell>
                      <TableCell>{dish.raw}</TableCell>
                      <TableCell>{dish.cooked}</TableCell>
                      {isFirstRow && (
                        <>
                          {isNutritionMenu && (
                            <>
                              <TableCell rowSpan={dishesCount}>
                                <>
                                  {day.total?.calorie ? (
                                    <FormattedNumber value={day.total.calorie} />
                                  ) : (
                                    ''
                                  )}
                                </>
                              </TableCell>
                              <TableCell rowSpan={dishesCount}>
                                <>
                                  {day.total?.protein ? (
                                    <FormattedNumber value={day.total.protein} />
                                  ) : (
                                    ''
                                  )}
                                </>
                              </TableCell>
                              <TableCell rowSpan={dishesCount}>
                                <>
                                  {day.total?.lipid ? (
                                    <FormattedNumber value={day.total.lipid} />
                                  ) : (
                                    ''
                                  )}
                                </>
                              </TableCell>
                              <TableCell rowSpan={dishesCount}>
                                <>
                                  {day.total?.glucid ? (
                                    <FormattedNumber value={day.total.glucid} />
                                  ) : (
                                    ''
                                  )}
                                </>
                              </TableCell>
                            </>
                          )}
                          {shouldShowAfternoonMeals ? (
                            <TableCell rowSpan={dishesCount}>
                              {day.afternoons.dishes.map((meal, idx) => {
                                if (!meal) return <>&nbsp; </>;

                                const dishName = (meal[0] || '').replace(/\d\./, '');
                                const manufacturer = meal[1] || '';

                                return (
                                  <Box sx={{ my: 1 }} key={idx}>
                                    <Typography
                                      sx={{
                                        userSelect: 'none',
                                        textAlign: 'center',
                                      }}
                                      variant="inherit"
                                    >
                                      {dishName}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        userSelect: 'none',
                                        textAlign: 'center',
                                      }}
                                      variant="inherit"
                                    >
                                      {manufacturer}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </TableCell>
                          ) : null}

                          {isNutritionMenu && shouldShowAfternoonMeals ? (
                            <TableCell rowSpan={dishesCount}>{day.afternoons.calorie}</TableCell>
                          ) : null}
                          <TableCell rowSpan={dishesCount}>{noteOfTheDay}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default MenuTable;
